import { resolveTaskMapping } from './task-mapping';
import { v4 as uuidv4 } from 'uuid';

import { ITitleInquiryPayload } from 'src/services/models/title-request.service';

interface IDealerInformation {
    dealerCode: string;
    requestorName: string;
    requestorEmail: string;
}

interface ITitleRequestInformation {
    reasonForRequest: string;
    documentRequestType: string;
    titleMaintenanceType: string;
    specialInstructions: string;
    isResubmission: boolean;
    uploadFile: File | null;
}

interface IAccountInformation {
    customerName: string;
    accountNumber: string;
    vin: string;
}

interface ITitleInformation {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
    purchaseDate: string;
    odometerMileage: string;
    odometerDate: string;
}

export interface IBuildPayloadParams {
    accountType: string;
    dealerInformation: IDealerInformation;
    titleRequestInformation: ITitleRequestInformation;
    accountInformation: IAccountInformation;
    titleInformation: ITitleInformation;
    isFieldUser: boolean;
    permissionsToken: string;
}

/**
 * Combines address fields into a single titleMailingAddress string.
 * Backend expects: max 150 chars, pattern: ^[A-Za-z0-9 '&\r\n/,.:-]{1,150}$
 */
function buildMailingAddress(titleInfo: ITitleInformation): string {
    const parts = [
        titleInfo.addressLine1,
        titleInfo.addressLine2,
        titleInfo.city,
        titleInfo.state,
        titleInfo.zipCode,
    ].filter(Boolean);

    return parts.join(', ');
}

/**
 * Formats a date string to YYYY-MM-DD format expected by the backend.
 */
function formatDateForApi(dateValue: string): string {
    if (!dateValue) return '';

    const date = new Date(dateValue);
    const year = date.getFullYear();
    // eslint-disable-next-line no-magic-numbers
    const month = String(date.getMonth() + 1).padStart(2, '0');
    // eslint-disable-next-line no-magic-numbers
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * Determines the selected request type based on the reason for request.
 */
function getSelectedRequestType(
    titleRequestInfo: ITitleRequestInformation,
): string {
    if (titleRequestInfo.reasonForRequest === 'Title Maintenance') {
        return titleRequestInfo.titleMaintenanceType;
    }

    return titleRequestInfo.documentRequestType;
}

/**
 * Builds the full ITitleInquiryPayload from form sections.
 */
export function buildTitleInquiryPayload(
    params: IBuildPayloadParams,
): ITitleInquiryPayload {
    const {
        accountType,
        dealerInformation,
        titleRequestInformation,
        accountInformation,
        titleInformation,
        isFieldUser,
        permissionsToken,
    } = params;

    const selectedRequestType = getSelectedRequestType(titleRequestInformation);

    const { taskId, specialInstructions } = resolveTaskMapping({
        reasonForRequest: titleRequestInformation.reasonForRequest,
        selectedRequestType,
        accountType,
        isResubmission: titleRequestInformation.isResubmission,
        isFieldUser,
        specialInstructions: titleRequestInformation.specialInstructions,
    });

    const odoMileage = titleInformation.odometerMileage
        ? parseInt(titleInformation.odometerMileage, 10)
        : null;

    return {
        accountType: accountType || null,
        dealerCode: dealerInformation.dealerCode,
        requestorName: dealerInformation.requestorName,
        requestorEmail: dealerInformation.requestorEmail,
        customerName: accountInformation.customerName || null,
        accountNumber: accountInformation.accountNumber || null,
        vin: accountInformation.vin,
        titleMailingAddress: buildMailingAddress(titleInformation),
        purchaseDate: formatDateForApi(titleInformation.purchaseDate),
        odoMileage,
        odometerDate: titleInformation.odometerDate
            ? formatDateForApi(titleInformation.odometerDate)
            : null,
        specialInstructions: specialInstructions || null,
        taskId,
        correlationId: uuidv4(),
        jwtToken: permissionsToken,
    };
}
